import { Auth } from "aws-amplify"
import {
  User,
  UserProfile,
  Message,
  Follow,
  ZeefFile,
  UpdateUserRequest,
  SendMessageRequest,
  CreateMessagePartRequest,
  PaginatedResponse,
  SearchUsersParams,
  GetMessagesParams,
  GetFollowsParams,
  FileUploadResponse,
  ZeefWebSocketConfig,
  ZeefEventObserver,
  WebSocketOutgoingMessage,
} from "./ZeefTypes"
import {
  ZeefWebSocketManager,
  ZeefWebSocketClient,
} from "./ZeefWebSocket"

// Base URL for the ZEEF API
const ZEEF_BASE_URL = process.env.REACT_APP_ZEEF_API_URL || ""

const ZeefService = {
  // Helper method to get authorization headers
  async getAuthHeaders(): Promise<{ Authorization: string; "Content-Type": string }> {
    const session = await Auth.currentSession()
    return {
      Authorization: `Bearer ${session.getIdToken().getJwtToken()}`,
      "Content-Type": "application/json",
    }
  },

  // Helper method for API calls with authentication
  async apiCall<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const headers = await this.getAuthHeaders()
    const response = await fetch(`${ZEEF_BASE_URL}${endpoint}`, {
      ...options,
      headers: {
        ...headers,
        ...options.headers,
      },
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(errorData.detail || `HTTP ${response.status}: ${response.statusText}`)
    }

    return response.json()
  },

  // ===== User Management Endpoints =====

  /**
   * Get the authenticated user's profile information
   */
  async getCurrentUser(): Promise<User> {
    return this.apiCall<User>("/api/users/me")
  },

  /**
   * Get detailed profile information for any user, including follow statistics
   */
  async getUserProfile(userId: string): Promise<UserProfile> {
    return this.apiCall<UserProfile>(`/api/users/${userId}`)
  },

  /**
   * Update the authenticated user's profile information
   */
  async updateCurrentUser(userData: UpdateUserRequest): Promise<User> {
    return this.apiCall<User>("/api/users/me", {
      method: "PUT",
      body: JSON.stringify(userData),
    })
  },

  /**
   * Search for users by username or email with pagination
   */
  async searchUsers(params: SearchUsersParams): Promise<PaginatedResponse<User>> {
    const { query, page = 1, limit = 20 } = params
    const searchParams = new URLSearchParams({
      query,
      page: page.toString(),
      limit: limit.toString(),
    })
    
    return this.apiCall<PaginatedResponse<User>>(`/api/users/?${searchParams}`)
  },

  /**
   * Get a list of currently online users (users with active WebSocket connections)
   */
  async getOnlineUsers(): Promise<User[]> {
    return this.apiCall<User[]>("/api/users/online")
  },

  // ===== Follow System Endpoints =====

  /**
   * Follow another user to receive their broadcast messages
   */
  async followUser(userId: string): Promise<Follow> {
    return this.apiCall<Follow>(`/api/follows/${userId}`, {
      method: "POST",
    })
  },

  /**
   * Stop following a user
   */
  async unfollowUser(userId: string): Promise<void> {
    await this.apiCall<void>(`/api/follows/${userId}`, {
      method: "DELETE",
    })
  },

  /**
   * Get paginated list of users who follow the authenticated user
   */
  async getFollowers(params: GetFollowsParams = {}): Promise<PaginatedResponse<User>> {
    const { page = 1, limit = 20 } = params
    const searchParams = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    })
    
    return this.apiCall<PaginatedResponse<User>>(`/api/follows/followers?${searchParams}`)
  },

  /**
   * Get paginated list of users the authenticated user is following
   */
  async getFollowing(params: GetFollowsParams = {}): Promise<PaginatedResponse<User>> {
    const { page = 1, limit = 20 } = params
    const searchParams = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    })
    
    return this.apiCall<PaginatedResponse<User>>(`/api/follows/following?${searchParams}`)
  },

  /**
   * Get paginated list of mutual follows (users who follow each other)
   */
  async getMutualFollows(params: GetFollowsParams = {}): Promise<PaginatedResponse<User>> {
    const { page = 1, limit = 20 } = params
    const searchParams = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    })
    
    return this.apiCall<PaginatedResponse<User>>(`/api/follows/mutual?${searchParams}`)
  },

  // ===== Messaging System Endpoints =====

  /**
   * Create and send a message with multiple parts (text, files, mentions)
   */
  async sendMessage(messageData: SendMessageRequest): Promise<Message> {
    return this.apiCall<Message>("/api/messages/", {
      method: "POST",
      body: JSON.stringify(messageData),
    })
  },

  /**
   * Get paginated list of messages visible to the authenticated user
   */
  async getMessages(params: GetMessagesParams = {}): Promise<PaginatedResponse<Message>> {
    const { page = 1, limit = 20, message_type } = params
    const searchParams = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    })
    
    if (message_type) {
      searchParams.append("message_type", message_type)
    }
    
    return this.apiCall<PaginatedResponse<Message>>(`/api/messages/?${searchParams}`)
  },

  /**
   * Get complete message details including all parts and recipients
   */
  async getMessageDetails(messageId: string): Promise<Message> {
    return this.apiCall<Message>(`/api/messages/${messageId}`)
  },

  /**
   * Mark a message as read by the authenticated user
   */
  async markMessageAsRead(messageId: string): Promise<void> {
    await this.apiCall<void>(`/api/messages/${messageId}/read`, {
      method: "PUT",
    })
  },

  /**
   * Get all messages in a threaded conversation (root message plus all replies)
   */
  async getThreadMessages(threadRootId: string): Promise<Message[]> {
    return this.apiCall<Message[]>(`/api/messages/thread/${threadRootId}`)
  },

  // ===== File Management Endpoints =====

  /**
   * Upload a file for use in message attachments
   */
  async uploadFile(file: File): Promise<FileUploadResponse> {
    const session = await Auth.currentSession()
    const formData = new FormData()
    formData.append("file", file)

    const response = await fetch(`${ZEEF_BASE_URL}/api/files/`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${session.getIdToken().getJwtToken()}`,
      },
      body: formData,
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(errorData.detail || `HTTP ${response.status}: ${response.statusText}`)
    }

    return response.json()
  },

  /**
   * Download a file attachment
   */
  async downloadFile(fileId: string): Promise<Blob> {
    const headers = await this.getAuthHeaders()
    const response = await fetch(`${ZEEF_BASE_URL}/api/files/${fileId}`, {
      headers: { Authorization: headers.Authorization },
    })

    if (!response.ok) {
      throw new Error(`Failed to download file: ${response.statusText}`)
    }

    return response.blob()
  },

  /**
   * Get metadata about a file without downloading it
   */
  async getFileInfo(fileId: string): Promise<ZeefFile> {
    return this.apiCall<ZeefFile>(`/api/files/${fileId}/info`)
  },

  /**
   * Delete an uploaded file (only if not used in any messages)
   */
  async deleteFile(fileId: string): Promise<void> {
    await this.apiCall<void>(`/api/files/${fileId}`, {
      method: "DELETE",
    })
  },

  // ===== WebSocket Methods =====

  /**
   * Initialize WebSocket connection with configuration
   */
  initializeWebSocket(config?: ZeefWebSocketConfig): ZeefWebSocketClient {
    const manager = ZeefWebSocketManager.getInstance()
    return manager.initialize(config)
  },

  /**
   * Get the current WebSocket client
   */
  getWebSocketClient(): ZeefWebSocketClient {
    const manager = ZeefWebSocketManager.getInstance()
    return manager.getClient()
  },

  /**
   * Connect to WebSocket for real-time events
   */
  async connectWebSocket(): Promise<void> {
    const manager = ZeefWebSocketManager.getInstance()
    await manager.connect()
  },

  /**
   * Disconnect WebSocket
   */
  disconnectWebSocket(): void {
    const manager = ZeefWebSocketManager.getInstance()
    manager.disconnect()
  },

  /**
   * Check if WebSocket is connected
   */
  isWebSocketConnected(): boolean {
    const manager = ZeefWebSocketManager.getInstance()
    return manager.isConnected()
  },

  /**
   * Send a message through WebSocket
   */
  async sendWebSocketMessage(message: WebSocketOutgoingMessage): Promise<void> {
    const manager = ZeefWebSocketManager.getInstance()
    await manager.sendMessage(message)
  },

  /**
   * Send a ping message to keep the connection alive
   */
  async sendWebSocketPing(): Promise<void> {
    const manager = ZeefWebSocketManager.getInstance()
    await manager.sendPing()
  },

  /**
   * Send a typing indicator
   */
  async sendWebSocketTyping(conversationId: string): Promise<void> {
    const manager = ZeefWebSocketManager.getInstance()
    await manager.sendTyping(conversationId)
  },

  /**
   * Send a user status request
   */
  async sendWebSocketUserStatus(): Promise<void> {
    const manager = ZeefWebSocketManager.getInstance()
    await manager.sendUserStatus()
  },

  /**
   * Add an event observer to receive WebSocket events
   */
  addWebSocketObserver(observer: any): void {
    const manager = ZeefWebSocketManager.getInstance()
    manager.addObserver(observer)
  },

  /**
   * Remove an event observer
   */
  removeWebSocketObserver(observer: any): void {
    const manager = ZeefWebSocketManager.getInstance()
    manager.removeObserver(observer)
  },

  // ===== Utility Methods =====

  /**
   * Helper method to create a text message part
   */
  createTextMessagePart(content: string, order = 0) {
    return {
      type: "text" as const,
      content,
      order,
    }
  },

  /**
   * Helper method to create a file message part
   */
  createFileMessagePart(fileId: string, filename: string, contentType: string, order = 0) {
    return {
      type: "file" as const,
      file_id: fileId,
      filename,
      content_type: contentType,
      order,
    }
  },

  /**
   * Helper method to create a mention message part
   */
  createMentionMessagePart(userId: string, displayName: string, order = 0) {
    return {
      type: "mention" as const,
      user_id: userId,
      display_name: displayName,
      order,
    }
  },

  /**
   * Helper method to create a simple text message
   */
  createSimpleTextMessage(content: string, type: "regular" | "broadcast" = "regular", threadRootId: string | null = null): SendMessageRequest {
    return {
      type,
      thread_root_id: threadRootId,
      parts: [this.createTextMessagePart(content, 0)],
    }
  },

  /**
   * Helper method to create a message with text and file attachments
   */
  createMultipartMessage(
    textContent: string,
    fileAttachments: Array<{ fileId: string; filename: string; contentType: string }>,
    type: "regular" | "broadcast" = "regular",
    threadRootId: string | null = null
  ): SendMessageRequest {
    const parts: CreateMessagePartRequest[] = [this.createTextMessagePart(textContent, 0)]
    
    fileAttachments.forEach((file, index) => {
      parts.push(this.createFileMessagePart(file.fileId, file.filename, file.contentType, index + 1))
    })
    
    return {
      type,
      thread_root_id: threadRootId,
      parts,
    }
  },

  /**
   * Helper method to get the display text for a message
   */
  getMessageDisplayText(message: Message): string {
    return message.parts
      .filter(part => part.type === "text" || part.type === "mention")
      .map(part => {
        if (part.type === "mention") {
          return part.display_name || ""
        }
        return part.content || ""
      })
      .join("")
  },

  /**
   * Helper method to get file attachments from a message
   */
  getMessageFileAttachments(message: Message): Array<{ fileId: string; filename: string; contentType: string }> {
    return message.parts
      .filter(part => part.type === "file")
      .map(part => ({
        fileId: part.file_id || "",
        filename: part.filename || "",
        contentType: part.content_type || "",
      }))
  },

  /**
   * Helper method to get mentions from a message
   */
  getMessageMentions(message: Message): Array<{ userId: string; displayName: string }> {
    return message.parts
      .filter(part => part.type === "mention")
      .map(part => ({
        userId: part.user_id || "",
        displayName: part.display_name || "",
      }))
  },
}

export default ZeefService
